import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { typeOf } from '@ember/utils';
import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsTextBody,
  HdsModal,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Typography, CoreModalConfirm } from 'core/components';
import { boxPadding } from 'core/modifiers';

/**
 * This shows or hides the modal based on truthiness.
 * @argument isActive
 * @type {Boolean}
 */

/**
 * A callback function to be called on a failure to remove users.
 * @argument onError
 * @type {Function}
 */

/**
 * A callback function to be called on a successful removal of users.
 * @argument onSuccess
 * @type {Function}
 */

/**
 * The organization context of the users that are being removed.
 * @argument organization
 * @type {Object}
 */

/**
 * The organization policy document.
 * @argument policy
 * @type {Object}
 */

/**
 * A list of users to remove.
 * @argument users
 * @type {Object}
 */
/**
 *
 * `PageUsersRemove` renders the modal for removing a user from an organization.
 *
 *
 * ```
 * <Page::Users::Remove
 *   @isActive={{showRemoveModal}}
 *   @organization={{@model.organization}}
 *   @onActiveChange={{resetRemoveUsers}}
 *   @onSuccess={{onRemoveUsersSuccess}}
 *   @policy={{@model.policy}}
 *   @users={{users}}
 * />
 * ```
 *
 * @class PageUsersRemove
 *
 */
export default class PageUsersRemove extends Component {
  @service api;
  @service operation;

  @tracked emails = null;

  get userEmails() {
    let { users } = this.args;

    if (users.length > 3) {
      return null;
    }

    return users
      .map(({ email }, index, self) => {
        return `${
          self.length > 1 && index === self.length - 1 ? 'and ' : ''
        }${email}`;
      })
      .join(', ');
  }

  removeUsers = task(async () => {
    let { organization, users } = this.args;
    let successfullyDeleted = [];
    let failedDeleted = [];

    for (let user of users) {
      let resp;

      try {
        resp = await this.api.iam.iamServiceDeleteOrganizationMembership(
          organization.id,
          user.id
        );

        successfullyDeleted.push({ resp, user });
      } catch (e) {
        if (macroCondition(isDevelopingApp())) {
          console.error(e);
        }

        failedDeleted.push({ error: e, resp, user });
      }
    }

    if (
      successfullyDeleted.length &&
      this.args.onSuccess &&
      typeOf(this.args.onSuccess) === 'function'
    ) {
      this.args.onSuccess({
        resp: successfullyDeleted.map((success) => success.resp),
        users: successfullyDeleted.map((success) => success.user),
      });
    }

    if (
      failedDeleted.length &&
      this.args.onError &&
      typeOf(this.args.onError) === 'function'
    ) {
      this.args.onError({
        error: failedDeleted.map((failed) => failed.error),
        resp: failedDeleted.map((failed) => failed.resp),
        users: failedDeleted.map((failed) => failed.user),
      });
    }
  });

  <template>
    {{#if @isActive}}
      {{#if (can "ssoDelete user" (get @users "0"))}}
        <CoreModalConfirm
          @confirmText={{t "components.core.modal.confirm.delete"}}
          @headerIcon="alert-diamond"
          @headerText={{t
            "components.page.access-control.users.delete.modal.title"
          }}
          @confirmHelp={{t
            "components.page.access-control.users.delete.modal.help-text"
            htmlSafe=true
          }}
          @confirmLabel={{t
            "components.page.access-control.users.delete.modal.label"
          }}
          @onSubmit={{this.removeUsers}}
          @onCancel={{@onActiveChange}}
        >
          <:body>
            <HdsTextBody @color="primary" @tag="p" {{boxPadding "0 0 md 0"}}>
              {{t
                "components.page.access-control.users.delete.modal.body1"
                userEmails=this.userEmails
                htmlSafe=true
              }}
            </HdsTextBody>
            <HdsTextBody @color="primary" @tag="p">
              {{t
                "components.page.access-control.users.delete.modal.ul"
                htmlSafe=true
              }}
              <ul>
                <li>
                  {{t "components.page.access-control.users.delete.modal.li"}}
                </li>
              </ul>
            </HdsTextBody>
            <HdsTextBody @color="primary" @tag="p" {{boxPadding "0 0 lg 0"}}>
              {{t
                "components.page.access-control.users.delete.modal.body2"
                htmlSafe=true
              }}
            </HdsTextBody>
          </:body>
        </CoreModalConfirm>
      {{else}}
        <HdsModal
          @color="critical"
          @onClose={{@onActiveChange}}
          data-test-user-remove-modal
          as |M|
        >
          <M.Header @icon="alert-triangle">
            {{t "components.page.access-control.users.remove.title"}}
          </M.Header>
          <M.Body>
            <form
              id="delete"
              class="hcp-hds-form"
              data-test-form
              {{on "submit" this.removeUsers.perform}}
            >
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                data-test-message
              >
                {{t
                  "components.page.access-control.users.remove.description"
                  numEmails=@users.length
                  userEmails=this.userEmails
                  organizationName=@organization.name
                  htmlSafe=true
                }}
              </Typography>
            </form>
          </M.Body>
          <M.Footer as |F|>
            <HdsButtonSet>
              <HdsButton
                @color="critical"
                @text={{t "components.form.remove"}}
                disabled={{this.removeUsers.isRunning}}
                form="delete"
                type="submit"
                data-test-submit
              />
              <HdsButton
                @color="secondary"
                @text={{t "components.form.cancel"}}
                disabled={{this.removeUsers.isRunning}}
                data-test-cancel
                {{on "click" F.close}}
              />
            </HdsButtonSet>
          </M.Footer>
        </HdsModal>
      {{/if}}
    {{/if}}
  </template>
}
